// src/routes/admin/platform/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import {
  Building2,
  ClipboardList,
  Landmark,
  type LucideIcon,
  MessagesSquare,
  Stethoscope,
  UserPlus,
  Users,
} from 'lucide-react';

import { BrandSwirl } from '@/assets/brand/BrandSwirl';
import { QuickActionCard, WorkspaceCard } from '@/components/admin';
import { useAuthStore } from '@/stores/authStore';
import { hasPermission, pickHighestAdminRole, type Permission, type RoleType } from '@/lib/rbac';
import { cn } from '@/lib/utils';

const ROLE_LABEL: Partial<Record<RoleType, string>> = {
  super_admin: 'Platform Super Admin',
};

export const Route = createFileRoute('/_staff/admin/platform/')({
  component: PlatformDashboard,
  staticData: { crumb: 'Home' },
});

interface WorkspaceEntry {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
  permission?: Permission;
}

const WORKSPACES: WorkspaceEntry[] = [
  {
    to: '/admin/platform/business-associates',
    icon: Building2,
    title: 'Business Associates',
    description: 'Tenants on the Myndwell platform and their top-level configuration.',
    permission: 'ba:read',
  },
  {
    to: '/admin/platform/organizations',
    icon: Landmark,
    title: 'Organizations',
    description: 'Every organization across every business associate.',
    permission: 'org:read',
  },
  {
    to: '/admin/platform/users',
    icon: Users,
    title: 'Users',
    description: 'All staff accounts across tenants.',
    permission: 'user:read',
  },
  {
    to: '/admin/platform/practitioners',
    icon: Stethoscope,
    title: 'Practitioners',
    description: 'Network of practitioners delivering care.',
    permission: 'practitioner:read',
  },
  {
    to: '/admin/platform/sessions',
    icon: MessagesSquare,
    title: 'Sessions',
    description: 'Session activity across the network.',
    permission: 'session:read',
  },
  {
    to: '/admin/platform/assessments',
    icon: ClipboardList,
    title: 'Assessments',
    description: 'Assessment templates and completion tracking.',
    permission: 'assessment:read',
  },
];

const QUICK_ACTIONS: WorkspaceEntry[] = [
  {
    to: '/admin/platform/business-associates/new',
    icon: Building2,
    title: 'Create business associate',
    description: 'Onboard a new tenant onto the platform.',
    // The platform surface is gated to super_admin (wildcard '*') — no need for
    // a more specific permission here. No role declares 'ba:create' literally.
  },
  {
    to: '/admin/platform/users',
    icon: UserPlus,
    title: 'Invite user',
    description: 'Send an invitation to join the platform.',
    permission: 'user:invite',
  },
  {
    to: '/admin/platform/sessions',
    icon: MessagesSquare,
    title: 'Review sessions today',
    description: 'See sessions across the network in real time.',
    permission: 'session:read',
  },
];

function timeOfDay(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function PlatformDashboard() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const firstName = user?.firstName ?? 'there';
  const adminRole = pickHighestAdminRole(role);
  const roleLabel = adminRole ? (ROLE_LABEL[adminRole] ?? adminRole) : '';

  const visibleWorkspaces = WORKSPACES.filter(
    (w) => !w.permission || hasPermission(role, w.permission),
  );
  const visibleActions = QUICK_ACTIONS.filter(
    (a) => !a.permission || hasPermission(role, a.permission),
  );

  const greeting = `Good ${timeOfDay(new Date().getHours())}, ${firstName}.`;

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className={cn('mx-auto max-w-6xl space-y-10')}>
      {/* Hero — mirrors the BA-admin workspace hero so both surfaces read as
         one family: bordered surface card, Roobert SemiMono greeting, ambient brand
         swirl on the right. Role label anchors context for the super admin. */}
      <header className="bg-surface relative overflow-hidden rounded-2xl border border-(--color-border) px-7 py-8 sm:px-9 sm:py-10">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-[44%] [mask-image:linear-gradient(to_left,black_30%,transparent_100%)] opacity-[0.22]"
        >
          <BrandSwirl className="h-full w-full" />
        </div>
        <div className="relative max-w-2xl">
          <div className="text-meta-sm tracking-eyebrow flex items-center gap-2 font-semibold text-neutral-400 uppercase">
            <span>{today}</span>
            {roleLabel ? (
              <>
                <span aria-hidden="true" className="text-neutral-300">
                  ·
                </span>
                <span className="text-accent-ink/70">{roleLabel}</span>
              </>
            ) : null}
          </div>
          <h1 className="font-display text-display-xl text-accent-ink mt-2 leading-[1.05] tracking-[-0.02em]">
            {greeting}
          </h1>
          <p className="text-body-md mt-3 max-w-xl leading-relaxed text-neutral-500">
            Managing tenants, organizations, and people across the Myndwell network.
          </p>
        </div>
      </header>

      {visibleWorkspaces.length > 0 ? (
        <section className="space-y-4">
          <h2 className="text-label-md tracking-label font-semibold text-neutral-500 uppercase">
            Workspaces
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleWorkspaces.map((w) => (
              <WorkspaceCard
                key={w.to}
                to={w.to}
                icon={w.icon}
                title={w.title}
                description={w.description}
                variant="platform"
              />
            ))}
          </div>
        </section>
      ) : null}

      {visibleActions.length > 0 ? (
        <section className="space-y-4">
          <h2 className="text-label-md tracking-label font-semibold text-neutral-500 uppercase">
            Quick actions
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {visibleActions.map((a) => (
              <QuickActionCard
                key={a.title}
                to={a.to}
                icon={a.icon}
                title={a.title}
                description={a.description}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
